import { useState } from 'react';
import { connectionsApi } from '@/api/connections';
import { useEditorStore } from '@/store/editorStore';
import type { Connection, DatabaseEngine, Environment } from '@/types/domain';
import './ConnectionModal.css';

interface Props {
  onClose: () => void;
  editConnection?: Connection;
}

interface FormState {
  name: string;
  engine: DatabaseEngine;
  host: string;
  port: number;
  database: string;
  environment: Environment;
  readOnly: boolean;
  tags: string;
  username: string;
  password: string;
}

const DEFAULT_PORT: Record<DatabaseEngine, number> = {
  MsSql: 1433,
  PostgreSql: 5432,
};

function initialForm(conn?: Connection): FormState {
  if (!conn) {
    return {
      name: '', engine: 'MsSql', host: '', port: 1433,
      database: '', environment: 'Development',
      readOnly: false, tags: '', username: '', password: '',
    };
  }
  return {
    name: conn.name,
    engine: conn.engine,
    host: conn.host,
    port: conn.port,
    database: conn.database,
    environment: conn.environment,
    readOnly: conn.readOnly,
    tags: conn.tags.join(', '),
    username: '',   // nunca exposto pelo backend
    password: '',   // vazio = mantém a senha atual
  };
}

export function ConnectionModal({ onClose, editConnection }: Props) {
  const loadConnections = useEditorStore(s => s.loadConnections);
  const isEdit = editConnection !== undefined;

  const [form, setForm] = useState<FormState>(() => initialForm(editConnection));
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleEngineChange(engine: DatabaseEngine) {
    setForm(prev => ({ ...prev, engine, port: DEFAULT_PORT[engine] }));
    setTestResult(null);
  }

  function handleChange<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
    setTestResult(null);
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      // Se estiver editando e não tiver redigitado as credenciais, usa as salvas no banco
      const useStoredCreds = isEdit && !form.username && !form.password;
      const result = useStoredCreds
        ? await connectionsApi.testById(editConnection!.id)
        : await connectionsApi.test({
            name: form.name || 'test',
            engine: form.engine,
            host: form.host,
            port: form.port,
            database: form.database,
            environment: form.environment,
            readOnly: form.readOnly,
            tags: [],
            username: form.username,
            password: form.password,
          });
      setTestResult(result);
    } catch {
      setTestResult({ success: false, message: 'Erro ao contactar o servidor.' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const payload = {
      name: form.name,
      engine: form.engine,
      host: form.host,
      port: form.port,
      database: form.database,
      environment: form.environment,
      readOnly: form.readOnly,
      tags: editConnection?.tags ?? [],
      username: form.username,
      password: form.password,
    };
    try {
      if (isEdit) {
        await connectionsApi.update(editConnection.id, payload);
      } else {
        await connectionsApi.create(payload);
      }
      await loadConnections();
      onClose();
    } catch {
      setError('Falha ao salvar conexão. Verifique os dados e tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="cm-overlay">
      <div className="cm-panel" onClick={e => e.stopPropagation()}>
        <div className="cm-header">
          <span className="cm-title">{isEdit ? 'Editar Conexão' : 'Nova Conexão'}</span>
          <button className="cm-close" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <form className="cm-form" onSubmit={handleSubmit}>
          <div className="cm-field">
            <label className="cm-label">Nome</label>
            <input
              className="cm-input"
              type="text"
              value={form.name}
              onChange={e => handleChange('name', e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="cm-field">
            <label className="cm-label">Engine</label>
            <select
              className="cm-input cm-select"
              value={form.engine}
              onChange={e => handleEngineChange(e.target.value as DatabaseEngine)}
            >
              <option value="MsSql">MsSql</option>
              <option value="PostgreSql">PostgreSql</option>
            </select>
          </div>

          <div className="cm-row">
            <div className="cm-field cm-field-grow">
              <label className="cm-label">Host</label>
              <input
                className="cm-input"
                type="text"
                placeholder="localhost"
                value={form.host}
                onChange={e => handleChange('host', e.target.value)}
                required
              />
            </div>
            <div className="cm-field cm-field-port">
              <label className="cm-label">Porta</label>
              <input
                className="cm-input"
                type="number"
                value={form.port}
                min={1}
                max={65535}
                onChange={e => handleChange('port', Number(e.target.value))}
                required
              />
            </div>
          </div>

          <div className="cm-field">
            <label className="cm-label">Database</label>
            <input
              className="cm-input"
              type="text"
              value={form.database}
              onChange={e => handleChange('database', e.target.value)}
              required
            />
          </div>

          <div className="cm-field">
            <label className="cm-label">Ambiente</label>
            <select
              className="cm-input cm-select"
              value={form.environment}
              onChange={e => handleChange('environment', e.target.value as Environment)}
            >
              <option value="Development">Development</option>
              <option value="Homologation">Homologation</option>
              <option value="Production">Production</option>
            </select>
          </div>

          <div className="cm-field">
            <label className="cm-label">Usuário</label>
            <input
              className="cm-input"
              type="text"
              value={form.username}
              onChange={e => handleChange('username', e.target.value)}
              required={!isEdit}
              placeholder={isEdit ? '(mantém o atual)' : ''}
            />
          </div>

          <div className="cm-field">
            <label className="cm-label">
              Senha{isEdit && <span className="cm-label-hint"> — deixe vazio para manter a atual</span>}
            </label>
            <div className="cm-password-wrap">
              <input
                className="cm-input cm-input-password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => handleChange('password', e.target.value)}
                placeholder={isEdit ? '(mantém a atual)' : ''}
              />
              <button
                type="button"
                className="cm-toggle-pw"
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? 'ocultar' : 'mostrar'}
              </button>
            </div>
          </div>

          <div className="cm-field cm-field-inline">
            <input
              id="cm-readonly"
              type="checkbox"
              checked={form.readOnly}
              onChange={e => handleChange('readOnly', e.target.checked)}
            />
            <label htmlFor="cm-readonly" className="cm-label cm-label-inline">
              Somente Leitura
            </label>
          </div>

          {testResult && (
            <div className={`cm-test-result ${testResult.success ? 'ok' : 'fail'}`}>
              <span className="cm-test-icon">{testResult.success ? '✓' : '✗'}</span>
              {testResult.message}
            </div>
          )}

          {error && <div className="cm-error">{error}</div>}

          <div className="cm-actions">
            <button
              type="button"
              className="cm-btn cm-btn-test"
              onClick={handleTest}
              disabled={testing || saving || !form.host || !form.database}
              title={!form.host || !form.database ? 'Preencha Host e Database' : ''}
            >
              {testing ? 'Testando...' : 'Testar conexão'}
            </button>
            <div className="cm-actions-right">
              <button type="button" className="cm-btn cm-btn-cancel" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="cm-btn cm-btn-save" disabled={saving}>
                {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Salvar conexão'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
